class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    // Wellness Resources: one-time items show a Subscribe & Save upsell;
    // subscribed items show a delivery-frequency selector.
    this.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-wr-selling-plan-toggle]');

      if (!toggle || !this.contains(toggle)) return;

      event.preventDefault();
      this.changeSellingPlan(toggle);
    });

    this.addEventListener(
      'change',
      (event) => {
        const select = event.target.closest('[data-wr-selling-plan-select]');

        if (!select || !this.contains(select)) return;

        // Stop Dawn's CartItems change handler from treating the
        // selling-plan selector as a quantity input.
        event.stopPropagation();

        this.changeSellingPlan(select);
      },
      true
    );

    this.setHeaderCartIconAccessibility();
  }

  async changeSellingPlan(control) {
    if (control.disabled) return;

    const line = Number(control.dataset.line);
    const quantity = Number(control.dataset.quantity);

    const sellingPlan =
      control.tagName === 'SELECT'
        ? control.value || ''
        : control.dataset.sellingPlan || '';

    if (!line || !quantity) {
      console.error('Missing cart line or quantity.', {
        line,
        quantity,
        sellingPlan,
      });
      return;
    }

    control.disabled = true;
    control.setAttribute('aria-busy', 'true');

    this.classList.add('wr-cart-plan-updating');

    const errorContainer =
      this.querySelector('#CartDrawer-CartErrors');

    if (errorContainer) {
      errorContainer.textContent = '';
    }

    try {
      /*
       * STEP 1
       * Change the cart line only.
       *
       * Do not request rendered sections as part of this request.
       * That can fail on app-proxy pages such as the Loop customer portal.
       */
      const changeResponse = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          selling_plan: sellingPlan
            ? Number(sellingPlan)
            : null,
        }),
      });

      const cartState = await changeResponse.json();

      if (!changeResponse.ok) {
        throw new Error(
          cartState.description ||
            cartState.message ||
            'Unable to update the purchase option.'
        );
      }

      if (cartState.errors) {
        throw new Error(
          typeof cartState.errors === 'string'
            ? cartState.errors
            : 'Unable to update the purchase option.'
        );
      }

      /*
       * STEP 2
       * Fetch fresh cart drawer HTML separately from the storefront root.
       *
       * This matches the AJAX section-refresh pattern already used
       * elsewhere in the theme.
       */
      const sectionUrl =
        window.Shopify.routes.root +
        '?sections=cart-icon-bubble,cart-drawer&_=' +
        Date.now();

      const sectionResponse = await fetch(sectionUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        cache: 'no-store',
      });

      if (!sectionResponse.ok) {
        throw new Error(
          'The cart updated, but the cart drawer could not be refreshed.'
        );
      }

      const sections =
        await sectionResponse.json();

      console.log(
        '[WR Cart] Returned cart sections:',
        sections
      );

      if (
        !sections ||
        !sections['cart-drawer'] ||
        !sections['cart-icon-bubble']
      ) {
        console.error(
          '[WR Cart] Required sections were not returned:',
          sections
        );

        throw new Error(
          'The cart updated, but Shopify did not return the cart drawer sections.'
        );
      }

      /*
       * STEP 3
       * Give the existing drawer render function the data structure
       * it expects.
       */
      cartState.sections = sections;

      this.renderContents(cartState);

      /*
       * Notify other theme components that the cart changed.
       */
      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined'
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'wr-selling-plan-control',
          cartData: cartState,
        });
      }

      document.dispatchEvent(
        new CustomEvent('cartUpdated', {
          detail: {
            message: 'Cart was updated',
            cart: cartState,
          },
        })
      );
    } catch (error) {
      console.error(
        '[WR Cart] Unable to change purchase option:',
        error
      );

      if (errorContainer) {
        errorContainer.textContent =
          error.message ||
          'There was a problem updating your cart. Please try again.';
      }
    } finally {
      this.classList.remove(
        'wr-cart-plan-updating'
      );

      /*
       * The drawer HTML may already have been replaced,
       * so only touch the original control if it still exists.
       */
      if (control.isConnected) {
        control.disabled = false;
        control.removeAttribute(
          'aria-busy'
        );
      }
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (!cartLink) return;

    cartLink.setAttribute(
      'role',
      'button'
    );

    cartLink.setAttribute(
      'aria-haspopup',
      'dialog'
    );

    cartLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        this.open(cartLink);
      }
    );

    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          event.preventDefault();

          this.open(cartLink);
        }
      }
    );
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(
        triggeredBy
      );
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute(
        'role'
      )
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    // Here the animation doesn't seem to always get triggered.
    // A timeout seems to help.
    setTimeout(() => {
      this.classList.add(
        'animate',
        'active'
      );
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains(
            'is-empty'
          )
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : document.getElementById(
                'CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        trapFocus(
          containerToTrapFocusOn,
          focusElement
        );
      },
      {
        once: true,
      }
    );

    document.body.classList.add(
      'overflow-hidden'
    );
  }

  close() {
    this.classList.remove(
      'active'
    );

    removeTrapFocus(
      this.activeElement
    );

    document.body.classList.remove(
      'overflow-hidden'
    );
  }

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling.getAttribute(
        'id'
      )
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote
          .nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    if (
      !parsedState ||
      !parsedState.sections
    ) {
      console.error(
        '[WR Cart] Cannot render drawer: sections missing.',
        parsedState
      );

      return;
    }

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    if (
      drawerInner &&
      drawerInner.classList.contains(
        'is-empty'
      )
    ) {
      drawerInner.classList.remove(
        'is-empty'
      );
    }

    this.productId =
      parsedState.id;

    this.getSectionsToRender().forEach(
      (section) => {
        const html =
          parsedState.sections[
            section.id
          ];

        if (!html) {
          console.error(
            `[WR Cart] Missing section HTML: ${section.id}`
          );

          return;
        }

        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (!sectionElement) {
          console.error(
            `[WR Cart] Could not find section element: ${section.id}`
          );

          return;
        }

        const newHTML =
          this.getSectionInnerHTML(
            html,
            section.selector
          );

        if (newHTML === null) {
          console.error(
            `[WR Cart] Could not parse section: ${section.id}`
          );

          return;
        }

        sectionElement.innerHTML =
          newHTML;
      }
    );

    setTimeout(() => {
      this.querySelector(
        '#CartDrawer-Overlay'
      )?.addEventListener(
        'click',
        this.close.bind(this)
      );

      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return null;

    const parsedDocument =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const element =
      parsedDocument.querySelector(
        selector
      );

    if (!element) {
      return null;
    }

    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define(
  'cart-drawer',
  CartDrawer
);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);